type IntersectionObserverHost = {
  IntersectionObserver?: typeof IntersectionObserver
}

const GUARD_FLAG = '__shiptanbulReentryGuard'

/**
 * iOS WebKit can invoke an IntersectionObserver callback synchronously inside
 * `observe()`. Floating UI's autoUpdate (Radix popovers, tooltips, selects)
 * responds by creating another observer and observing again, so the two
 * functions recurse until the stack overflows.
 *
 * Nested callbacks are dropped. The observer that was just created still
 * receives later, asynchronous notifications.
 */
export function installIntersectionObserverGuard(host: IntersectionObserverHost = window): void {
  const NativeIntersectionObserver = host.IntersectionObserver
  if (typeof NativeIntersectionObserver !== 'function') return
  if (GUARD_FLAG in NativeIntersectionObserver) return

  let depth = 0

  const GuardedIntersectionObserver = function (
    callback: IntersectionObserverCallback,
    options?: IntersectionObserverInit
  ): IntersectionObserver {
    const guarded: IntersectionObserverCallback = (entries, observer) => {
      if (depth > 0) return
      depth += 1
      try {
        callback(entries, observer)
      } finally {
        depth -= 1
      }
    }
    return new NativeIntersectionObserver(guarded, options)
  } as unknown as typeof IntersectionObserver

  GuardedIntersectionObserver.prototype = NativeIntersectionObserver.prototype
  Object.defineProperty(GuardedIntersectionObserver, GUARD_FLAG, { value: true })
  host.IntersectionObserver = GuardedIntersectionObserver
}
